import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { isPlatform, useSessionUser } from '@/shared/session/currentUser';
import { DestinationName } from '@/shared/triage/DestinationName';
import { CATEGORIES, CATEGORY_LABELS } from '@/shared/triage/vocabulary';
import { AiNotice } from '@/shared/ui/AiNotice';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { CardList, CardListItem } from '@/shared/ui/CardList';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Select } from '@/shared/ui/Input';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { Table, TBody, Td, Th, THead, Tr } from '@/shared/ui/Table';
import { CheckIcon, InboxIcon, PaperclipIcon, PenIcon, SearchIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';
import { useDebounced } from '@/shared/utils/useDebounced';

import type { EmailListParams } from './api';
import { CategoryBadge } from './CategoryBadge';
import { ComposeDialog, JustSent } from './Compose';
import { formatAgo, formatWhen } from './dates';
import { mailboxShortLabel, senderLines } from './inboxLabels';
import { InboxPicker, InboxRail } from './InboxRail';
import { AnsweredBadge } from './Answered';
import { OutOfInboxBadge } from './OutOfInbox';
import { highlight, includedTerms } from './searchTerms';
import { SpamBadge } from './SpamBadge';
import { StatusBadge } from './StatusBadge';
import {
  isUnread,
  decisionCategory,
  liveDecision,
  MANUAL_MAILBOX,
  mailboxLabel,
  type Email,
  type IngestStats,
  type TriageDecision,
} from './types';
import {
  useEmailList,
  useInboxViews,
  useIngestStats,
  useMailboxes,
  useReadAll,
  useUnreadCount,
} from './useEmails';
import {
  categoryFrom,
  emailHref,
  type InboxPlace,
  listFilters,
  offsetFrom,
  PAGE_SIZE,
  PARAM,
  placeFrom,
  placeLabel,
} from './views';

const fullDateFormat = new Intl.DateTimeFormat('es-ES', {
  dateStyle: 'long',
  timeStyle: 'short',
});

const HELP = [
  <>
    <strong>Por revisar</strong> es lo que nadie ha respondido todavía: cuando está vacío, has
    terminado. Al pulsar «Correcto» dentro de un correo, sale de aquí y va a su carpeta, en la
    aplicación y en tu correo (Thunderbird, el webmail): las carpetas de la izquierda son las
    mismas.
  </>,
  <>
    Lo que sacas de la bandeja de entrada <strong>en tu programa de correo</strong> (lo mueves a
    otra carpeta o lo borras) también sale de «Por revisar» en un minuto: ya lo has atendido allí.
    Sigue en «Todos», marcado «Fuera de la bandeja».
  </>,
  <>
    <strong>El sistema propone y una persona confirma.</strong> Nada se reenvía ni se mueve hasta
    que alguien pulsa «Correcto» o «Corregir…» dentro del correo; el botón dice lo que va a pasar.
    «Devolver a la bandeja» lo deshace; «Eliminar» lo lleva a la Papelera, de donde se recupera.
  </>,
  <>
    <strong>Redactar</strong> escribe un correo nuevo desde uno de tus buzones, con copia en su
    carpeta de Enviados. Para contestar uno que ha llegado, ábrelo y pulsa «Responder».
  </>,
  <>
    <strong>Buscar</strong> encuentra los correos que tienen <strong>todas</strong> las palabras en
    el remitente, el asunto, el texto o el nombre de un adjunto, sin importar tildes ni mayúsculas.
    Entre comillas, una frase tal cual («"pedido 4471"»); con un guion delante, una palabra que no
    debe salir («-publicidad»).
  </>,
  <>
    <strong>En negrita y con un punto azul</strong>, lo que nadie ha leído todavía. Leído es del
    buzón, como en tu programa de correo: lo que abre cualquiera que lo ve, aquí o allí, sale leído
    para todos. «No leídos» deja solo esos; «Marcar todo como leído» los da por vistos, todas las
    páginas de lo que estás filtrando. <strong>Respondido</strong> es que ya salió una respuesta,
    desde aquí o desde el programa de correo.
  </>,
  <>
    <strong>Clasificando…</strong> es que acaba de llegar y el sistema aún no lo ha leído.{' '}
    <strong>Con error</strong> sale a la izquierda solo cuando hay alguno: ábrelo y pulsa
    «Reprocesar». <strong>Spam del servidor</strong> es lo que tu proveedor dejó en su carpeta de
    spam; si algo es bueno, se rescata desde el correo.
  </>,
  <>
    <strong>A quién</strong> es el destino que propone el sistema. «sin revisar» significa que nadie
    ha dicho todavía si está bien; el check verde, que alguien lo confirmó; «corregido», que alguien
    eligió otro destino.
  </>,
  <>
    La bandeja se actualiza sola cada minuto. Si un correo está en el buzón y aquí no pasados cinco
    minutos, avisa a quien administra la aplicación con el asunto y la hora.
  </>,
];

/**
 * Why the page is empty. Past the first page it is never "there are no emails": the
 * total shrank under the offset while the user was looking at a later page.
 */
function emptyStateCopy({
  place,
  filtered,
  offset,
  noMailbox,
  search,
  unreadOnly,
}: {
  place: InboxPlace;
  filtered: boolean;
  offset: number;
  noMailbox: boolean;
  search: string;
  unreadOnly: boolean;
}): {
  title: string;
  hint: string;
} {
  if (noMailbox) {
    // Contract v10.12: a person sees the mailboxes assigned to them and nothing else.
    return {
      title: 'No tienes ningún buzón asignado',
      hint: 'Pídele a quien administra la aplicación que te asigne el buzón que lees. Hasta entonces esta bandeja está vacía.',
    };
  }
  if (offset > 0) {
    return {
      title: 'Esta página ya no tiene correos',
      hint: 'Han salido de la lista mientras la mirabas. Vuelve con «Anteriores».',
    };
  }
  if (unreadOnly) {
    return {
      title: 'Nada sin leer',
      hint: 'Todo lo que hay con estos filtros está leído. Quita «No leídos» para verlo.',
    };
  }
  if (search) {
    return {
      title: `Ningún correo con «${search}»`,
      hint: 'Tienen que salir todas las palabras. Prueba con menos, o sin los filtros de estado, buzón y «Qué es».',
    };
  }
  if (filtered) {
    return {
      title: 'Ningún correo con ese filtro',
      hint: 'Prueba con otro «Qué es», o mira en otra carpeta.',
    };
  }
  return EMPTY_PLACES[place.view](place);
}

/** What an empty place means, place by place (contract v10.25). */
const EMPTY_PLACES: Record<
  InboxPlace['view'],
  (place: InboxPlace) => { title: string; hint: string }
> = {
  por_revisar: () => ({
    title: 'Nada por revisar',
    hint: 'Has respondido todo lo que ha llegado. Lo nuevo aparecerá aquí en cuanto llegue; la lista se actualiza sola cada minuto.',
  }),
  carpeta: (place) => ({
    title: `Nada en «${place.folder ?? ''}»`,
    hint: 'Aquí aparece lo que confirmes de esta clase: «Correcto» lo mueve a esta carpeta, también en tu correo.',
  }),
  todos: () => ({
    title: 'Todavía no hay correos',
    hint: 'Irán apareciendo aquí en cuanto lleguen al buzón. La lista se actualiza sola cada minuto.',
  }),
  papelera: () => ({
    title: 'La papelera está vacía',
    hint: 'Lo que elimines desde un correo va a la Papelera de tu buzón, y desde aquí se recupera.',
  }),
  spam: () => ({
    title: 'Nada en la carpeta de spam',
    hint: 'Aquí sale lo que tu proveedor de correo dejó en su carpeta de spam.',
  }),
  errores: () => ({
    title: 'Ningún correo con error',
    hint: 'Todo lo que ha llegado se ha podido leer.',
  }),
};

/** The newest `last_ingested_at` across the mailboxes, or null when none has one. */
function lastIngestedAt(stats: IngestStats | undefined): string | null {
  const stamps = (stats?.mailboxes ?? [])
    .map((box) => box.last_ingested_at)
    .filter((stamp): stamp is string => Boolean(stamp))
    .sort();
  return stamps.at(-1) ?? null;
}

/**
 * The one line above the list that says how the system stands: when the last mail came
 * in, how many mailboxes. Amber when the connector has gone quiet, because then the list
 * is not going to grow and the person waiting for a mail should know.
 *
 * It used to open with «Modo: solo propone, todavía no reenvía ni etiqueta nada», which
 * stopped being true with contract v10 and is the opposite of v10.25, where «Correcto»
 * moves the mail. What a confirmation does is said per email, on its button.
 */
function StateLine({ stats }: { stats: IngestStats | undefined }) {
  const mailboxes = stats?.mailboxes ?? null;
  const stale = mailboxes?.filter((box) => box.stale) ?? [];
  if (stale.length > 0) {
    const minutes = Math.round((stats?.stale_after_seconds ?? 300) / 60);
    const where = stale.length === 1 ? stale[0]?.mailbox : `${stale.length} buzones`;
    return (
      <Callout tone="warn" compact>
        El lector del correo lleva más de {minutes} min sin dar señales en {where}. Los correos
        nuevos no aparecerán hasta que vuelva; el administrador recibe el aviso.
      </Callout>
    );
  }
  // Nothing until the stats are in, then the line: it sits above the rail and the list,
  // so appearing a second later does not move a row under the cursor.
  const boxes = mailboxes?.length ?? null;
  const last = lastIngestedAt(stats);
  const parts = [
    last ? (
      <span key="last">
        Último correo:{' '}
        <time dateTime={last} title={formatDate(fullDateFormat, last)}>
          {formatAgo(last)}
        </time>
      </span>
    ) : null,
    boxes === null ? null : (
      <span key="boxes">
        {boxes === 0
          ? 'ningún buzón conectado todavía'
          : boxes === 1
            ? '1 buzón'
            : `${boxes} buzones`}
      </span>
    ),
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <Callout tone="neutral" compact role="status">
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 ? <span className="text-ink-4 mx-1.5">·</span> : null}
          {part}
        </span>
      ))}
    </Callout>
  );
}

/**
 * «A quién»: the destination, and whether a person has said anything about it. The
 * proposal in the quiet ink until somebody reviews it; the final destination in full ink
 * once somebody did, with a green check when they agreed and «corregido» when they did
 * not. Nothing for an email nobody classified: the status badge already says why.
 *
 * Each person has a coloured dot in front of their name (`DestinationName`), and an
 * unreviewed «Se queda» goes one ink quieter still: it is most of the inbox, and the rows
 * that go to someone are the ones a person scans the column for.
 */
function Destination({
  decision,
  inline = false,
}: {
  decision?: TriageDecision | undefined;
  inline?: boolean;
}) {
  if (!decision) return null;
  const final = decision.final_destination ?? null;
  if (!final) {
    return (
      <span className={cn(inline ? 'inline-flex items-baseline gap-1.5' : 'flex flex-col')}>
        <DestinationName
          destination={decision.proposed_destination}
          className={decision.proposed_destination === 'se_queda' ? 'text-ink-3' : 'text-ink-2'}
        />
        <span className={cn('text-ink-4 text-[12px]', !inline && 'pl-3.5')}>sin revisar</span>
      </span>
    );
  }
  if (final === decision.proposed_destination) {
    return (
      <span className="text-ink inline-flex items-center gap-1">
        <DestinationName destination={final} />
        <CheckIcon size={14} className="text-ok" />
        <span className="sr-only">revisado</span>
      </span>
    );
  }
  return (
    <span className={cn(inline ? 'inline-flex items-baseline gap-1.5' : 'flex flex-col')}>
      <DestinationName destination={final} className="text-ink" />
      <span className={cn('text-ink-3 text-[12px]', !inline && 'pl-3.5')}>corregido</span>
    </span>
  );
}

/** The paperclip next to the subject; the count is for the screen reader and the tooltip. */
function Attachments({ count }: { count: number }) {
  if (count === 0) return null;
  const words = count === 1 ? '1 adjunto' : `${count} adjuntos`;
  return (
    <span className="text-ink-3 inline-flex shrink-0 items-center" title={words}>
      <PaperclipIcon size={14} />
      <span className="sr-only">{words}</span>
    </span>
  );
}

/**
 * `text` with the searched words marked, as written: accents and capitals stay, only the
 * comparison ignores them. Without terms it is the plain text.
 */
function Marked({ text, terms }: { text: string; terms: string[] }) {
  if (terms.length === 0) return <>{text}</>;
  return (
    <>
      {highlight(text, terms).map((piece, index) =>
        piece.match ? (
          <mark key={index} className="bg-warn-soft text-ink rounded-[2px]">
            {piece.text}
          </mark>
        ) : (
          <Fragment key={index}>{piece.text}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * Where in the body the words are, when that is where they are (contract v10.21). The
 * back sends it only with a search and only for a match in the body: a match in the
 * sender, the subject or an attachment is already on the row.
 */
function Snippet({ text, terms }: { text?: string | null | undefined; terms: string[] }) {
  if (!text) return null;
  return (
    // Two lines, not one: the back puts a few words before the match, and in the narrow
    // «Asunto» column a single truncated line cut off the very word that was found.
    <p className="text-ink-3 line-clamp-2 text-[12.5px] break-words" data-testid="match-snippet">
      <Marked text={text} terms={terms} />
    </p>
  );
}

/**
 * «De»: the name, and under it in the quiet ink the address it was sent from. Both are
 * marked, because the search looks in both. The tooltip is the whole sender, for when
 * the column cuts it.
 */
function Sender({ email, terms }: { email: Email; terms: string[] }) {
  const { primary, secondary } = senderLines(email);
  const unread = isUnread(email);
  return (
    <div
      className="flex items-start gap-1.5"
      title={secondary ? `${primary} <${secondary}>` : primary}
    >
      <UnreadDot unread={unread} className="mt-[7px]" />
      <div className="min-w-0">
        <p className={cn('text-ink truncate', unread && 'font-semibold')}>
          <Marked text={primary} terms={terms} />
        </p>
        {secondary ? (
          <p className="text-ink-4 truncate text-[12px]" data-testid="sender-address">
            <Marked text={secondary} terms={terms} />
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The blue dot of an email the person has not opened (contract v10.22). Blue, not red:
 * red is the one button that acts, and a coloured dot per person already marks «A quién».
 * The space is kept on a read row too, so the names do not jump when one is opened.
 */
function UnreadDot({ unread, className }: { unread: boolean; className?: string }) {
  return (
    <span className={cn('inline-flex size-2 shrink-0', className)}>
      {unread ? (
        <>
          <span
            aria-hidden="true"
            className="bg-info size-2 rounded-full"
            data-testid="unread-dot"
          />
          <span className="sr-only">No leído</span>
        </>
      ) : null}
    </span>
  );
}

/**
 * «No leídos», a switch next to the status filter (contract v10.22), with how many rows
 * it would leave. Its own control and not a fifth segment: it adds to the status, it does
 * not replace it («Con error» and unread is a real question).
 */
function UnreadToggle({
  on,
  count,
  onChange,
}: {
  on: boolean;
  count: number | undefined;
  onChange: (on: boolean) => void;
}) {
  return (
    <div className="bg-paper-2 border-line inline-flex rounded-lg border p-1">
      <button
        type="button"
        aria-pressed={on}
        onClick={() => onChange(!on)}
        className={cn(
          'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium whitespace-nowrap transition-colors',
          'focus-visible:outline-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1',
          on ? 'bg-card text-ink shadow-card' : 'text-ink-3 hover:text-ink',
        )}
      >
        <span aria-hidden="true" className="bg-info size-1.5 rounded-full" />
        No leídos
        {count !== undefined ? (
          <span
            className={cn(
              'rounded-full px-1.5 text-[11.5px] tabular-nums',
              on ? 'bg-paper-2 text-ink-2' : 'bg-card/60 text-ink-3',
            )}
          >
            {count}
          </span>
        ) : null}
      </button>
    </div>
  );
}

/** A mailbox that is a whole address breaks after the @ when it wraps, not mid-word. */
function MailboxName({ label }: { label: string }) {
  const at = label.lastIndexOf('@');
  if (at <= 0) return <>{label}</>;
  return (
    <>
      {label.slice(0, at + 1)}
      <wbr />
      {label.slice(at + 1)}
    </>
  );
}

/** Badges by exception: the category always, the status only when it is not the normal one. */
function RowBadges({ email, decision }: { email: Email; decision?: TriageDecision | undefined }) {
  return (
    <>
      {email.status !== 'processed' ? (
        <StatusBadge status={email.status} retrying={email.retrying ?? false} />
      ) : null}
      {decision ? <CategoryBadge category={decisionCategory(decision)} wrap /> : null}
      <SpamBadge email={email} />
      <OutOfInboxBadge email={email} />
      <AnsweredBadge email={email} />
    </>
  );
}

/**
 * The notice of contract v10.25, once, until «Entendido»: from now on confirming also
 * files the mail in the person's own mailbox. Remembered in this browser only, which is
 * enough for a notice (a new browser shows it once more) and never blocks anything: a
 * browser that refuses storage shows it every time, with its button.
 */
const MOVE_NOTICE_KEY = 'cubas.aviso.correcto-ordena-el-buzon';

function readNoticeSeen(): boolean {
  try {
    return window.localStorage.getItem(MOVE_NOTICE_KEY) === '1';
  } catch {
    return false;
  }
}

function MoveNotice() {
  const [seen, setSeen] = useState(readNoticeSeen);
  if (seen) return null;
  function dismiss(): void {
    try {
      window.localStorage.setItem(MOVE_NOTICE_KEY, '1');
    } catch {
      // Private window or blocked storage: the notice goes away for this visit only.
    }
    setSeen(true);
  }
  return (
    <Callout
      tone="info"
      title="Novedad: lo que confirmes aquí también se ordena en tu correo"
      action={
        <Button size="sm" variant="secondary" onClick={dismiss}>
          Entendido
        </Button>
      }
    >
      Al pulsar «Correcto», el mensaje sale de la bandeja de entrada de tu buzón (Thunderbird, el
      webmail) y va a su carpeta, marcado como leído. No se borra nada: si te equivocas, «Devolver a
      la bandeja» lo deja como estaba.
    </Callout>
  );
}

/**
 * Triage inbox (contract v10.25): a rail of places on the left, «Por revisar» first, and
 * the list of the chosen place, searchable and filterable by mailbox and «Qué es».
 *
 * Everything is in the address (`views.ts`): the place, the mailbox, the search, «Qué
 * es», «No leídos» and the page. A row opens its email with the list's address, so the
 * detail knows which list «Anterior» and «Siguiente» walk and «← Bandeja» comes back to
 * it. The search box answers every key and the address follows it 250 ms later
 * (`useDebounced`), replacing the entry: one search is one place in the history.
 */
export function EmailsPage() {
  const me = useSessionUser();
  const noMailbox = me !== undefined && !isPlatform(me) && (me.mailboxes ?? []).length === 0;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const place = placeFrom(searchParams);
  const mailbox = searchParams.get(PARAM.mailbox) ?? '';
  const category = categoryFrom(searchParams.get(PARAM.category));
  const unreadOnly = searchParams.get(PARAM.unread) === '1';
  const offset = offsetFrom(searchParams);
  const [typed, setTyped] = useState(() => searchParams.get(PARAM.search) ?? '');
  // «Redactar» (contract v10.40): the form, and the line that follows what it sent.
  const [composing, setComposing] = useState(false);
  const [justSent, setJustSent] = useState<string | null>(null);
  const search = useDebounced(typed).trim();

  /** Set (or clear) keys of the address, back to the first page, replacing the entry. */
  function update(changes: Record<string, string | null>, { keepPage = false } = {}): void {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        if (!keepPage) next.delete(PARAM.page);
        return next;
      },
      { replace: true },
    );
  }

  const inUrl = searchParams.get(PARAM.search) ?? '';
  useEffect(() => {
    if (inUrl === search) return;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (search) next.set(PARAM.search, search);
        else next.delete(PARAM.search);
        next.delete(PARAM.page);
        return next;
      },
      { replace: true },
    );
  }, [inUrl, search, setSearchParams]);

  function goToPage(nextOffset: number): void {
    const page = Math.floor(nextOffset / PAGE_SIZE) + 1;
    update({ [PARAM.page]: page > 1 ? String(page) : null }, { keepPage: true });
  }

  const terms = includedTerms(search);
  // What the list, «Marcar todo como leído» and the unread count share. The address is
  // the source (`listFilters`), with the search as typed-and-settled.
  const filters: EmailListParams = {
    ...listFilters(searchParams),
    ...(search ? { search } : {}),
  };
  // «No leídos» apart: the switch says how many it would leave, so it asks without itself.
  const withoutRead: EmailListParams = { ...filters };
  delete withoutRead.read;
  const params: EmailListParams = { ...filters, limit: PAGE_SIZE, offset };
  const emails = useEmailList(params);
  const mailboxes = useMailboxes();
  const ingest = useIngestStats();
  const views = useInboxViews(mailbox);
  const unreadCount = useUnreadCount(withoutRead);
  const readAll = useReadAll();
  // A superadministrator marks nothing (v10.43): read is the mailbox's, and looking into
  // somebody's inbox must not change it. The switch and the count stay.
  const platform = isPlatform(me);
  // The list says whether the back knows about read marks (v10.22).
  const readMarks =
    unreadOnly || (emails.data?.items.some((email) => email.is_read !== undefined) ?? false);

  const total = emails.data?.total ?? 0;
  const lastOnPage = Math.min(offset + PAGE_SIZE, total);
  const showPager = offset > 0 || total > PAGE_SIZE;

  const items = emails.data?.items ?? [];
  // «Buzón» is a column only while the list can mix mailboxes (see `MailboxName`).
  const knownMailboxes = mailboxes.data?.items ?? [...new Set(items.map((email) => email.mailbox))];
  const showMailbox =
    !mailbox && (mailboxes.data ? mailboxes.data.total > 1 : knownMailboxes.length > 1);
  const hrefOf = (id: string) => emailHref(id, searchParams);

  return (
    <>
      <PageHeader
        title="Bandeja"
        description={placeLabel(place)}
        help={HELP}
        actions={
          <Button icon={<PenIcon size={17} />} onClick={() => setComposing(true)}>
            Redactar
          </Button>
        }
      />
      {justSent ? <JustSent id={justSent} onDismiss={() => setJustSent(null)} /> : null}
      {composing ? (
        <ComposeDialog
          kind="new"
          sourceEmailId={null}
          onClose={() => setComposing(false)}
          onSent={(outgoing) => setJustSent(outgoing.id)}
        />
      ) : null}

      <MoveNotice />
      <StateLine stats={ingest.data} />

      <div className="grid gap-5 xl:grid-cols-[12rem_minmax(0,1fr)] xl:items-start">
        <InboxRail data={views.data} current={place} params={searchParams} />

        <div className="flex min-w-0 flex-col gap-4">
          <InboxPicker data={views.data} current={place} params={searchParams} />

          <div className="relative w-full lg:max-w-xl">
            <SearchIcon
              size={16}
              className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
            />
            <input
              type="search"
              aria-label="Buscar correos"
              placeholder="Buscar por remitente, asunto, texto o adjunto"
              className="field w-full pl-9"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
            {readMarks ? (
              <div className="flex flex-wrap items-center gap-2">
                <UnreadToggle
                  on={unreadOnly}
                  count={unreadCount}
                  onChange={(on) => update({ [PARAM.unread]: on ? '1' : null })}
                />
                {unreadCount && !platform ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<CheckIcon size={15} />}
                    disabled={readAll.isPending}
                    onClick={() => readAll.mutate(withoutRead)}
                  >
                    {readAll.isPending ? 'Marcando…' : 'Marcar todo como leído'}
                  </Button>
                ) : readAll.data && readAll.data.marked > 0 ? (
                  <span role="status" className="text-ink-3 text-[13px]">
                    {readAll.data.marked === 1
                      ? '1 correo marcado como leído'
                      : `${readAll.data.marked} correos marcados como leídos`}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex">
              <div className="lg:w-56">
                <Select
                  label="Buzón"
                  value={mailbox}
                  onChange={(event) => update({ [PARAM.mailbox]: event.target.value || null })}
                >
                  <option value="">Todos los buzones</option>
                  {mailboxes.data?.items.map((name) => (
                    <option key={name} value={name}>
                      {mailboxLabel(name)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="lg:w-56">
                <Select
                  label="Qué es"
                  value={category}
                  onChange={(event) =>
                    update({ [PARAM.category]: categoryFrom(event.target.value) || null })
                  }
                >
                  <option value="">Todo</option>
                  {CATEGORIES.map((value) => (
                    <option key={value} value={value}>
                      {CATEGORY_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          {mailboxes.isError ? (
            <ErrorMessage error={mailboxes.error} tone="warning">
              No se pudo cargar la lista de buzones
            </ErrorMessage>
          ) : null}
          {views.isError ? (
            <ErrorMessage error={views.error} tone="warning">
              No se pudieron contar las carpetas
            </ErrorMessage>
          ) : null}

          {emails.isPending ? <Spinner variant="block" label="Cargando correos" /> : null}

          {emails.isError ? (
            <ErrorMessage error={emails.error}>No se pudo cargar la bandeja</ErrorMessage>
          ) : null}
          {readAll.isError ? (
            <ErrorMessage error={readAll.error}>No se pudo marcar todo como leído</ErrorMessage>
          ) : null}

          {emails.data && items.length === 0 ? (
            <EmptyState
              icon={<InboxIcon size={24} />}
              {...emptyStateCopy({
                place,
                // The mailbox is where one looks, not a filter: an empty «Por revisar» of
                // compras is still «Nada por revisar».
                filtered: Boolean(category),
                offset,
                noMailbox,
                search,
                unreadOnly,
              })}
            />
          ) : null}

          {items.length > 0 ? (
            <>
              {/* Desktop: the table. The whole row opens the email; the subject is a real
                  link on top of that, so the middle click and «abrir en pestaña nueva» keep
                  working for somebody going through 150 a day. Out of the tab order: the row
                  is the stop, the link would be a second one per row. */}
              <div className="bg-card border-line rounded-card shadow-card hidden border lg:block">
                <Table label="Correos" minWidth="0">
                  <THead>
                    <tr>
                      {/* Widths measured at 1280 px with the rail (2026-09-24): «Asunto»
                          keeps some 230 px without the «Buzón» column. */}
                      <Th className="w-[11rem]">De</Th>
                      {showMailbox ? <Th className="w-[6.5rem]">Buzón</Th> : null}
                      <Th>Asunto</Th>
                      <Th className="w-[9.5rem]">Qué es</Th>
                      <Th className="w-[9.5rem]">A quién</Th>
                      <Th className="w-[6rem] text-right">Llegó</Th>
                    </tr>
                  </THead>
                  <TBody>
                    {items.map((email) => {
                      const decision = liveDecision(email);
                      const to = hrefOf(email.id);
                      return (
                        <Tr key={email.id} interactive onClick={() => void navigate(to)}>
                          <Td className="max-w-0">
                            <Sender email={email} terms={terms} />
                          </Td>
                          {showMailbox ? (
                            <Td className="max-w-0 text-[13px]">
                              <p
                                className="text-ink-3 line-clamp-2 break-words"
                                title={mailboxLabel(email.mailbox)}
                              >
                                <MailboxName
                                  label={mailboxShortLabel(email.mailbox, knownMailboxes)}
                                />
                              </p>
                            </Td>
                          ) : null}
                          <Td className="max-w-0">
                            <p className="flex items-center gap-1.5">
                              <Link
                                to={to}
                                tabIndex={-1}
                                onClick={(event) => event.stopPropagation()}
                                className={cn(
                                  'text-ink min-w-0 truncate hover:underline',
                                  isUnread(email) ? 'font-semibold' : 'font-normal',
                                )}
                              >
                                <Marked text={email.subject ?? '(sin asunto)'} terms={terms} />
                              </Link>
                              <Attachments count={email.attachments?.length ?? 0} />
                            </p>
                            <Snippet text={email.match_snippet} terms={terms} />
                          </Td>
                          <Td>
                            <span className="flex flex-wrap gap-1">
                              <RowBadges email={email} decision={decision} />
                            </span>
                          </Td>
                          <Td className="text-[13.5px]">
                            <Destination decision={decision} />
                          </Td>
                          <Td className="text-ink-3 text-right text-[13px] whitespace-nowrap">
                            <time dateTime={email.received_at}>
                              {formatWhen(email.received_at)}
                            </time>
                          </Td>
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>
              </div>

              {/* Phone: one card per email, the whole card is the link. */}
              <CardList label="Correos" className="lg:hidden">
                {items.map((email) => {
                  const decision = liveDecision(email);
                  return (
                    <CardListItem
                      key={email.id}
                      to={hrefOf(email.id)}
                      title={
                        <span className="flex items-center gap-1.5">
                          {isUnread(email) ? <UnreadDot unread /> : null}
                          <span
                            className={cn(
                              'min-w-0 truncate',
                              isUnread(email) ? 'font-semibold' : 'font-normal',
                            )}
                          >
                            <Marked text={email.subject ?? '(sin asunto)'} terms={terms} />
                          </span>
                          <Attachments count={email.attachments?.length ?? 0} />
                        </span>
                      }
                      subtitle={
                        // The phone has room for one line: the sender's name, and «a compras»
                        // for the mailbox, with the «a» so it cannot pass for the sender's
                        // address. An upload has no «a».
                        <>
                          <Marked text={senderLines(email).primary} terms={terms} />
                          {showMailbox ? (
                            <span className="text-ink-4">
                              {email.mailbox === MANUAL_MAILBOX ? ' · ' : ' · a '}
                              {mailboxShortLabel(email.mailbox, knownMailboxes)}
                            </span>
                          ) : null}
                        </>
                      }
                      aside={<RowBadges email={email} decision={decision} />}
                      footer={
                        <>
                          <time dateTime={email.received_at}>{formatWhen(email.received_at)}</time>
                          <span className="text-[13px]">
                            <Destination decision={decision} inline />
                          </span>
                        </>
                      }
                    >
                      {email.match_snippet ? (
                        <Snippet text={email.match_snippet} terms={terms} />
                      ) : null}
                    </CardListItem>
                  );
                })}
              </CardList>
            </>
          ) : null}

          {showPager ? (
            <div className="text-ink-3 flex items-center justify-end gap-3 text-[13px]">
              <span className="tabular-nums">
                {total === 0 ? 0 : offset + 1}–{lastOnPage} de {total}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => goToPage(Math.max(0, offset - PAGE_SIZE))}
              >
                Anteriores
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={lastOnPage >= total}
                onClick={() => goToPage(offset + PAGE_SIZE)}
              >
                Siguientes
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <AiNotice className="mt-auto" />
    </>
  );
}
